<script setup lang="ts">
  import type { FormInst, FormRules } from 'naive-ui'
  import { useMessage } from 'naive-ui'

  export type NewsFormModel = {
    title: string
    slug: string
    cover: string
    excerpt: string
    body: string
    author: string
    sourceUrl: string
    /** ms timestamp for n-date-picker; null = empty */
    publishTime: number | null
    status: number
  }

  const props = withDefaults(
    defineProps<{
      mode?: 'create' | 'edit'
      loading?: boolean
      initialValue?: Partial<NewsFormModel> | null
    }>(),
    {
      mode: 'create',
      loading: false,
      initialValue: null,
    },
  )

  const emit = defineEmits<{
    submit: [payload: {
      title: string
      slug: string
      cover: string | null
      excerpt: string | null
      body: string
      author: string | null
      sourceUrl: string | null
      publishTime: string | null
      status: number
    }]
    cancel: []
  }>()

  const message = useMessage()
  const formRef = ref<FormInst | null>(null)
  const coverUploading = ref(false)

  /** System-generated unique slug, e.g. news-1721812345678-a1b2c3 */
  function createUniqueSlug() {
    const rand = Math.random().toString(36).slice(2, 8)
    return `news-${Date.now()}-${rand}`
  }

  function startOfTodayMs() {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }

  function createEmptyForm(): NewsFormModel {
    return {
      title: '',
      slug: createUniqueSlug(),
      cover: '',
      excerpt: '',
      body: '',
      author: '',
      sourceUrl: '',
      publishTime: startOfTodayMs(),
      status: 0,
    }
  }

  const form = ref<NewsFormModel>(createEmptyForm())

  watch(
    () => props.initialValue,
    (value) => {
      const empty = createEmptyForm()
      form.value = {
        ...empty,
        ...(value || {}),
        // Create: always use system slug; edit: keep existing
        slug: props.mode === 'edit' && value?.slug ? value.slug : empty.slug,
      }
    },
    { immediate: true, deep: true },
  )

  const rules: FormRules = {
    title: [{ required: true, message: '请输入标题', trigger: ['blur', 'input'] }],
    cover: [
      {
        required: true,
        trigger: ['change', 'blur'],
        validator: (_rule, value: string) => {
          if (!value || !String(value).trim()) {
            return new Error('请上传封面图')
          }
          return true
        },
      },
    ],
    body: [{ required: true, message: '请输入正文', trigger: ['blur', 'input'] }],
    status: [{ type: 'number', required: true, message: '请选择状态', trigger: ['change', 'blur'] }],
  }

  function onTitleUpdate(value: string) {
    form.value.title = value
  }

  function formatPublishTime(ms: number | null) {
    if (ms === null || Number.isNaN(ms)) return null
    const d = new Date(ms)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }

  async function onSubmit() {
    if (coverUploading.value) {
      message.warning('封面正在上传，请稍候')
      return
    }

    if (!form.value.slug.trim()) {
      form.value.slug = createUniqueSlug()
    }

    // WangEditor empty doc often becomes <p><br></p>
    const plainBody = form.value.body
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim()
    if (!plainBody) {
      message.warning('请输入正文')
      return
    }

    try {
      await formRef.value?.validate()
    } catch {
      message.warning('请完善表单信息')
      return
    }

    emit('submit', {
      title: form.value.title.trim(),
      slug: form.value.slug.trim().toLowerCase(),
      cover: form.value.cover.trim() || null,
      excerpt: form.value.excerpt.trim() || null,
      body: form.value.body.trim(),
      author: form.value.author.trim() || null,
      sourceUrl: form.value.sourceUrl.trim() || null,
      publishTime: formatPublishTime(form.value.publishTime),
      status: form.value.status,
    })
  }
</script>

<template>
  <n-form
    ref="formRef"
    class="news-form"
    :model="form"
    :rules="rules"
    label-placement="left"
    label-width="96"
    require-mark-placement="right-hanging"
  >
    <n-grid :cols="24" :x-gap="16" :y-gap="4">
      <n-form-item-gi :span="24" label="标题" path="title">
        <n-input
          :value="form.title"
          maxlength="255"
          show-count
          placeholder="请输入新闻标题"
          @update:value="onTitleUpdate"
        />
      </n-form-item-gi>

      <n-form-item-gi :span="12" label="作者" path="author">
        <n-input v-model:value="form.author" maxlength="64" placeholder="作者 / 来源部门" />
      </n-form-item-gi>

      <n-form-item-gi :span="12" label="状态" path="status">
        <n-select v-model:value="form.status" :options="NEWS_STATUS_OPTIONS" />
      </n-form-item-gi>

      <n-form-item-gi :span="12" label="发布时间" path="publishTime">
        <n-date-picker
          v-model:value="form.publishTime"
          type="datetime"
          clearable
          style="width: 100%"
        />
      </n-form-item-gi>

      <n-form-item-gi :span="12" label="封面图" path="cover">
        <AppAdminImageUpload
          v-model="form.cover"
          v-model:uploading="coverUploading"
          :disabled="loading"
        />
      </n-form-item-gi>

      <n-form-item-gi :span="24" label="来源链接" path="sourceUrl">
        <n-input v-model:value="form.sourceUrl" maxlength="512" placeholder="原文 / 外部链接（可选）" />
      </n-form-item-gi>

      <n-form-item-gi :span="24" label="摘要" path="excerpt">
        <n-input
          v-model:value="form.excerpt"
          type="textarea"
          maxlength="512"
          show-count
          :rows="3"
          placeholder="列表摘要（可选）"
        />
      </n-form-item-gi>

      <n-form-item-gi :span="24" label="正文" path="body">
        <ClientOnly>
          <AppAdminRichEditor v-model="form.body" :disabled="loading" />
          <template #fallback>
            <n-skeleton height="420px" :sharp="false" />
          </template>
        </ClientOnly>
      </n-form-item-gi>
    </n-grid>

    <div class="news-form__actions">
      <n-button :disabled="loading" @click="emit('cancel')">取消</n-button>
      <n-button type="primary" :loading="loading" @click="onSubmit">
        {{ mode === 'create' ? '创建' : '保存' }}
      </n-button>
    </div>
  </n-form>
</template>

<style lang="scss" scoped>
  .news-form__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
    padding-top: 8px;
  }
</style>
